import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import {
  ESdkEvents,
  EventParams,
  SubscriptionStatus,
  GetSubscriptionResponse,
} from '../../../../contracts';
import { sendFEEvent } from '../../../../eventUtil';
import { useAPI } from '../../../../services/api.service';
import { useCheckout } from '../../../../hooks/use-checkout';
import { ECheckoutStatus } from '../../../../constants/enums';
import { usePaymentState } from '../../../Checkout/hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { useCheckoutFlowType } from '../../../Checkout/hooks/use-checkout-flow-type';
import { logError, logInfo } from '../../../../utils/coralogix-rum';

const FETCH_INTERVAL_TIME = 2500;
const AXIOS_NETWORK_ERR_CODE = 'ERR_NETWORK';
const MINUTE_IN_MILLISECONDS = 60000;
const CONNECTION_LOST_REASON = 'Connection lost';
const SUBSCRIPTION_POLLING_STARTED_LOG = 'Subscription polling started';
const SUBSCRIPTION_POLLING_STATUS_CHANGED_LOG = 'Subscription polling status changed';
const SUBSCRIPTION_POLLING_SUCCEEDED_LOG = 'Subscription polling succeeded';
const SUBSCRIPTION_POLLING_FAILED_LOG = 'Subscription polling failed';
const SUBSCRIPTION_POLLING_CONNECTION_LOST_LOG = 'Subscription polling connection lost';

export function useSubscriptionPolling() {
  const { API } = useAPI();
  const interval = useRef<NodeJS.Timeout>();
  const connectionLostTimestamp = useRef<number>(0);
  const lastSubscriptionStatus = useRef<SubscriptionStatus>();
  const { getEventParams } = useCheckout();
  const { featureFlags, publisherId } = useAppConfig();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const { isPaymentLinksFlow } = useCheckoutFlowType();

  const startSubscriptionPolling = (
    subscriptionId: string,
    FEeventParams: Partial<EventParams> = {}
  ) => {
    connectionLostTimestamp.current = Date.now();
    lastSubscriptionStatus.current = undefined;
    if (interval.current) {
      clearInterval(interval.current);
    }
    setCheckoutStatus(ECheckoutStatus.PENDING);
    logInfo(SUBSCRIPTION_POLLING_STARTED_LOG, {
      ...getSubscriptionPollingLogData(subscriptionId),
      fetchIntervalMs: FETCH_INTERVAL_TIME,
    });
    interval.current = setInterval(
      fetchSubscriptionData,
      FETCH_INTERVAL_TIME,
      subscriptionId,
      FEeventParams
    );
  };

  async function fetchSubscriptionData(
    subscriptionId: string,
    FEeventParams?: Partial<EventParams>
  ) {
    try {
      const subscriptionResponse = await API.getSubscription(subscriptionId);
      handleSubscriptionResponse(subscriptionResponse, FEeventParams);
    } catch (error: any) {
      if (error.code === AXIOS_NETWORK_ERR_CODE) {
        handleConnectionLost(subscriptionId, FEeventParams);
      } else {
        clearInterval(interval.current);
        logError(SUBSCRIPTION_POLLING_FAILED_LOG, error, {
          ...getSubscriptionPollingLogData(subscriptionId),
        });
        sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
          ...getEventParams(),
          ...FEeventParams,
          error: {
            isFatal: true,
          },
        });
        showBoundary(t('somethingWentWrong'));
      }
    }
  }

  function handleSubscriptionResponse(
    subscription: GetSubscriptionResponse,
    FEeventParams?: Partial<EventParams>
  ) {
    if (subscription?.status !== lastSubscriptionStatus.current) {
      lastSubscriptionStatus.current = subscription?.status;
      logInfo(SUBSCRIPTION_POLLING_STATUS_CHANGED_LOG, {
        ...getSubscriptionPollingLogData(subscription?.subscriptionId, subscription?.status),
      });
    }

    if (subscription?.status === SubscriptionStatus.ACTIVE) {
      logInfo(SUBSCRIPTION_POLLING_SUCCEEDED_LOG, {
        ...getSubscriptionPollingLogData(subscription.subscriptionId, subscription.status),
      });
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      const nextCheckoutStatus =
        featureFlags?.checkout_disable_success_screen && !isPaymentLinksFlow
          ? ECheckoutStatus.SUCCESS_SHOW_PENDING
          : ECheckoutStatus.SUCCESS;
      setCheckoutStatus(nextCheckoutStatus);
      clearInterval(interval.current);
    }
  }

  function handleConnectionLost(subscriptionId: string, FEeventParams?: Partial<EventParams>) {
    if (Date.now() - connectionLostTimestamp.current >= MINUTE_IN_MILLISECONDS) {
      clearInterval(interval.current);
      logError(SUBSCRIPTION_POLLING_CONNECTION_LOST_LOG, undefined, {
        ...getSubscriptionPollingLogData(subscriptionId),
        reason: CONNECTION_LOST_REASON,
      });
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: CONNECTION_LOST_REASON,
        error: {
          isFatal: true,
          reason: CONNECTION_LOST_REASON,
        },
      });
      showBoundary(t('checkTheGameForYourItems'));
    }
  }

  function getSubscriptionPollingLogData(subscriptionId?: string, subscriptionStatus?: SubscriptionStatus) {
    const eventParams = getEventParams();

    return {
      publisherId,
      sessionId: eventParams.sessionId,
      subscriptionId,
      subscriptionStatus,
    };
  }

  return { startSubscriptionPolling };
}
